import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Alert, Box, Group, List, Text } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import {
  useDeleteSegment,
  usePinArtwork,
  usePublishSegment,
  useSegmentAction,
  useSegmentJoin,
  useSettings,
  useSplitProposal,
  useSplitSegment,
  useStarSegment,
  useUnpinArtwork,
  useUpdateSegment,
} from "../api/admin";
import { useMe } from "../api/queries";
import type { Segment } from "../api/types";
import { nativeRuntime } from "../platform/native";
import { handleNativeLink } from "../platform/nativeLinks";
import { usePlayer } from "../state/player";
import { msToZonedText } from "../time/parse";
import { useZone } from "../time/useZone";
import { useUndo } from "./undo";
import { Artwork } from "../ui/Artwork";
import { ArtworkPicker } from "../ui/ArtworkPicker";
import { Chip } from "../ui/Chip";
import { confirm } from "../ui/confirm";
import { FeedbackLine } from "../ui/FeedbackLine";

/**
 * How a panel runs a write and where a refusal is printed.
 *
 * `report` is the line the message lands on. The unarchive and publish chips keep their own,
 * because a refusal printed a screen further down a panel that scrolls is one nobody reads.
 */
export type Run = (
  work: () => Promise<unknown>,
  report?: (message: string | null) => void,
) => Promise<void>;

// In a joined show, either as a later section or as the first one with sections behind it.
// Both spellings mean the same offer -- unjoining dissolves the whole group whichever of
// its sections asks -- so the chip appears on all of them.
function isJoined(segment: Segment): boolean {
  return segment.join_head_id !== null || segment.join_seconds !== null;
}

/** Whether jukebox tracks are archive material, from the setting `approve` is checked against. */
function useJukeboxApprovable(): boolean {
  const { data } = useSettings();
  return data?.find((setting) => setting.key === "treat_jukebox_as_streams")?.value === true;
}

/** The notice an exported show wears, and the one way back out of that state. */
export function ExportedNotice({ segment, run }: { segment: Segment; run: Run }) {
  const { t } = useTranslation("admin");
  // Not folded in with the review actions: this one is the way out of the exported state
  // rather than a move within the review flow, and it is the only segment action that removes
  // a file from the archive disk.
  const unarchive = useSegmentAction("unarchive");
  // The one refusal unarchiving can meet -- the buffer has aged past this show, so the copy
  // on disk cannot be cut again -- and it belongs beside the chip that asked for it.
  const [unarchiveError, setUnarchiveError] = useState<string | null>(null);

  return (
    // The notice carries the way out of the exported state, so it holds a chip as well as
    // prose.
    <Alert color="yellow" variant="light">
      <Text size="xs" mb={8}>
        {t("inspector.boundariesLocked")}{" "}
        {segment.unarchive_blocker
          ? t("inspector.alreadyOutOfBuffer")
          : t("inspector.mayUnarchive")}
      </Text>
      <FeedbackLine>{unarchiveError}</FeedbackLine>
      <Chip
        // Greyed from the server's own check rather than from a rule re-derived here:
        // whether the buffer still holds those hours is a question about files on disk,
        // and a second opinion computed from a retention setting would be wrong the
        // moment the two disagreed.
        disabled={unarchive.isPending || segment.unarchive_blocker !== null}
        title={
          // The refusal arrives from the server already translated, so it goes in as an
          // interpolated reason rather than being glued onto an English sentence.
          segment.unarchive_blocker
            ? t("inspector.cannotUnarchive", { reason: segment.unarchive_blocker })
            : t("inspector.unarchiveHint")
        }
        onClick={() => void run(() => unarchive.mutateAsync(segment.id), setUnarchiveError)}
      >
        {t("inspector.unarchive")}
      </Chip>
    </Alert>
  );
}

/** The verdict on a row: save what was typed, approve it, put it back, keep its audio. */
export function ReviewChips({
  segment,
  run,
  dirty,
  timesValid,
  saving,
  onSave,
}: {
  segment: Segment;
  run: Run;
  dirty: boolean;
  timesValid: boolean;
  saving: boolean;
  onSave: () => void;
}) {
  const { t } = useTranslation("admin");
  const approve = useSegmentAction("approve");
  const unapprove = useSegmentAction("unapprove");
  const reset = useSegmentAction("reset");
  const star = useStarSegment();
  // Approve is refused for a jukebox track unless the operator has opted in, so the chip is
  // only offered where the server would take it.
  const jukeboxApprovable = useJukeboxApprovable();
  const approvable = segment.kind === "show" || jukeboxApprovable;
  const exported = segment.state === "exported";
  const joined = isJoined(segment);

  return (
    <Group gap={6} mb={10}>
      <Chip variant="accent" disabled={!dirty || !timesValid || saving} onClick={onSave}>
        {t("common.save")}
      </Chip>
      {/* Approve is a verdict on a show's metadata; star keeps a jukebox track's audio past
          the buffer. Offering both on every row made them read as two spellings of one
          decision, which is the one thing they are not. */}
      {approvable &&
        (segment.state === "approved" ? (
          <Chip
            disabled={exported}
            title={joined ? t("inspector.approveJoinHint") : undefined}
            onClick={() => void run(() => unapprove.mutateAsync(segment.id))}
          >
            {t("inspector.unapprove")}
          </Chip>
        ) : (
          <Chip
            disabled={exported}
            title={joined ? t("inspector.approveJoinHint") : undefined}
            onClick={() => void run(() => approve.mutateAsync(segment.id))}
          >
            {t("inspector.approve")}
          </Chip>
        ))}
      <Chip
        disabled={exported}
        title={t("inspector.resetHint")}
        onClick={() => void run(() => reset.mutateAsync(segment.id))}
      >
        {t("inspector.resetToDetected")}
      </Chip>
      {/* Also when already starred, whatever the kind: a show starred before this rule
          existed still needs a way back off. */}
      {(segment.kind === "track" || segment.starred) && (
        <Chip
          pressed={segment.starred}
          title={t("inspector.starHint")}
          onClick={() =>
            void run(() => star.mutateAsync({ id: segment.id, starred: !segment.starred }))
          }
        >
          {segment.starred ? t("inspector.starred") : t("inspector.star")}
        </Chip>
      )}
    </Group>
  );
}

/** What a row is made of: cut it, join it to its neighbour, take it apart, delete it. */
export function StructureChips({
  segment,
  run,
  timesValid,
  report,
}: {
  segment: Segment;
  run: Run;
  timesValid: boolean;
  report: (message: string | null) => void;
}) {
  const { t } = useTranslation("admin");
  const { data: me } = useMe();
  const split = useSplitSegment();
  const joinNext = useSegmentJoin("join-next");
  const unjoin = useSegmentJoin("unjoin");
  const remove = useDeleteSegment();
  const exported = segment.state === "exported";
  const joined = isJoined(segment);

  return (
    <Group gap={6} mb={10}>
      <Chip
        disabled={exported || !timesValid}
        title={t("inspector.splitHint")}
        onClick={() =>
          void run(async () => {
            const at = usePlayer.getState().positionMs;
            if (at <= Date.parse(segment.start) || at >= Date.parse(segment.end)) {
              report(t("inspector.playheadOutside"));
              return;
            }
            await split.mutateAsync({ id: segment.id, at: new Date(at).toISOString() });
          })
        }
      >
        {t("inspector.splitAtPlayhead")}
      </Chip>
      {segment.kind === "show" && (
        <Chip
          disabled={exported}
          title={t("inspector.joinHint")}
          // Which show is "next" is the server's question, not this panel's: it is the one
          // after the group's *last* section, so pressing this twice joins a broadcast that
          // was cut into three.
          onClick={() => void run(() => joinNext.mutateAsync(segment.id))}
        >
          {t("inspector.joinWithNext")}
        </Chip>
      )}
      {joined && (
        <Chip
          title={t("inspector.unjoinHint")}
          onClick={() => void run(() => unjoin.mutateAsync(segment.id))}
        >
          {t("inspector.unjoin")}
        </Chip>
      )}
      {me?.role === "admin" && (
        <Chip
          variant="danger"
          // Matched tracks are archived inside their show's file and have none of their
          // own, so there is nothing for deleting one to orphan -- the server says the
          // same. `export_path` is not published, and a matched track only ever has one
          // if it was starred and exported before the show claimed it.
          disabled={segment.audio_state === "archived" && segment.matched_in_id === null}
          title={t("inspector.deleteHint")}
          onClick={() =>
            void run(async () => {
              // Unconditional on kind rather than on whether this show has matched tracks:
              // the panel does not know that, and finding out costs a query for a sentence.
              const warning =
                segment.kind === "show"
                  ? t("inspector.confirmDeleteShow", { id: segment.id })
                  : t("inspector.confirmDelete", { id: segment.id });
              if (!(await confirm(warning, t("common.delete"), true))) {
                return;
              }
              await remove.mutateAsync(segment.id);
              usePlayer.getState().select(null);
            })
          }
        >
          {t("common.delete")}
        </Chip>
      )}
    </Group>
  );
}

/** Post the show to the channels it is configured for, and where it has been posted already. */
export function PublishPanel({ segment, run }: { segment: Segment; run: Run }) {
  const { t } = useTranslation("admin");
  const publish = usePublishSegment();
  const [publishError, setPublishError] = useState<string | null>(null);
  const runtime = nativeRuntime();

  return (
    <>
      <Group gap={6} mb={10}>
        <Chip
          // Greyed from the server's own check, exactly as the unarchive chip is: whether a
          // target is signed in and configured is a question about settings this panel does
          // not read. The blocker arrives already translated, so it is shown verbatim.
          disabled={segment.publish_blocker !== null || publish.isPending}
          title={segment.publish_blocker ?? t("inspector.publishHint")}
          onClick={() =>
            void run(async () => {
              const done = (segment.posts ?? []).filter((p) => p.state === "posted");
              if (
                done.length > 0 &&
                !(await confirm(
                  t("inspector.confirmRepost", {
                    targets: done.map((p) => p.target).join(", "),
                  }),
                  t("inspector.postToChannels"),
                ))
              ) {
                return;
              }
              await publish.mutateAsync({ id: segment.id, repost: done.length > 0 });
            }, setPublishError)
          }
        >
          {publish.isPending ? t("inspector.queueing") : t("inspector.postToChannels")}
        </Chip>
      </Group>
      <FeedbackLine role="alert">{publishError}</FeedbackLine>
      {(segment.posts ?? []).length > 0 && (
        // Where a show has been posted, under the chip that queued it.
        <Box component="ul" fz={12} style={{ listStyle: "none", margin: "6px 0 0", padding: 0 }}>
          {segment.posts!.map((post) => {
            const state = t(`inspector.postState.${post.state}`, { defaultValue: post.state });
            const url = post.url;
            return (
              <li key={post.target}>
                <Box
                  component="span"
                  style={{ fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace' }}
                >
                  {post.target}
                </Box>{" "}
                {url ? (
                  <a
                    href={url}
                    target="_blank"
                    rel="noreferrer"
                    onClick={(event) =>
                      void handleNativeLink(event, runtime, (native) => native.openExternal(url))
                    }
                  >
                    {state}
                  </a>
                ) : (
                  state
                )}
                {post.note && (
                  <Text component="span" fz="inherit" c="dimmed">
                    {" "}
                    · {post.note}
                  </Text>
                )}
                <FeedbackLine role="alert" fz="inherit">
                  {post.error}
                </FeedbackLine>
              </li>
            );
          })}
        </Box>
      )}
    </>
  );
}

/** Where the segmenter thinks this row should be cut, when it has an opinion. */
export function SplitProposal({ segment, run }: { segment: Segment; run: Run }) {
  const { t } = useTranslation("admin");
  const zone = useZone();
  const { data: proposal } = useSplitProposal(segment.id);
  const split = useSplitSegment();
  const exported = segment.state === "exported";
  if (!proposal || proposal.boundaries.length === 0) return null;

  return (
    <Box
      p={10}
      my={10}
      fz={12}
      style={{
        border: "1px dashed var(--mantine-color-warn-filled)",
        borderRadius: "var(--mantine-radius-md)",
      }}
    >
      <strong>{t("inspector.splitSuggested")}</strong>
      <List listStyleType="none" style={{ margin: "8px 0 0", padding: 0, display: "grid", gap: 6 }}>
        {proposal.boundaries.map((boundary) => (
          <List.Item key={boundary}>
            <Chip
              disabled={exported}
              onClick={() => void run(() => split.mutateAsync({ id: segment.id, at: boundary }))}
            >
              {t("inspector.splitAt", { time: msToZonedText(Date.parse(boundary), zone.name) })}
            </Chip>
          </List.Item>
        ))}
      </List>
    </Box>
  );
}

/** The cover this row wears, and how far a chosen one is made to reach. */
export function ArtworkPanel({
  segment,
  run,
  defaultCover,
}: {
  segment: Segment;
  run: Run;
  defaultCover: string | null;
}) {
  const { t } = useTranslation("admin");
  const update = useUpdateSegment();
  const pin = usePinArtwork();
  const unpin = useUnpinArtwork();
  const [picking, { open: pick, close: stopPicking }] = useDisclosure(false);
  // What the server will scope a pin to. A show the matcher never linked and whose billing is
  // blank has nothing to pin against, and the endpoint answers 400 -- so do not offer it.
  const pinnable =
    segment.kind === "track"
      ? segment.track_identity_id !== null
      : segment.show_id !== null || !!segment.djs;

  const chooseArtwork = (artworkId: number | null) =>
    run(async () => {
      const before = segment.artwork_id;
      await update.mutateAsync({ id: segment.id, patch: { artwork_id: artworkId } });
      useUndo.getState().push({
        label: t("inspector.undoArtwork", { id: segment.id }),
        run: () => update.mutateAsync({ id: segment.id, patch: { artwork_id: before } }),
      });
    });

  const pinArtwork = () =>
    run(async () => {
      if (segment.artwork_id === null) return;
      // Deliberately no undo entry: a pin writes across every airing, and an undo that only
      // put this one row back would be a lie about what it reverted.
      await pin.mutateAsync({ id: segment.id, artworkId: segment.artwork_id });
    });

  return (
    <>
      <Group gap={8}>
        <Artwork
          src={segment.artwork_url ?? defaultCover}
          seed={segment.title ?? segment.raw_title ?? "untitled"}
          size={64}
          alt=""
        />
        <Chip onClick={pick}>{t("inspector.artworkPicker")}</Chip>
        <Chip onClick={() => void pinArtwork()} disabled={segment.artwork_id === null || !pinnable}>
          {segment.kind === "show" ? t("inspector.useForDjShows") : t("inspector.useForAirings")}
        </Chip>
        {segment.artwork_pinned && (
          <Chip
            variant="danger"
            // No undo entry, for the same reason a pin has none: this takes the cover off
            // every airing that had it, and putting one row back would misrepresent what
            // was reverted.
            onClick={() => void run(() => unpin.mutateAsync(segment.id))}
          >
            {t("inspector.unpin")}
          </Chip>
        )}
      </Group>
      {picking && (
        <ArtworkPicker
          current={segment.artwork_id}
          onClose={stopPicking}
          onPick={(artworkId) => {
            stopPicking();
            void chooseArtwork(artworkId);
          }}
        />
      )}
    </>
  );
}
